import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Label, TextInput, TextArea, ImageUploader, Section, BulletEditor } from "../FormControls";

const Hero = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.hero.get().then(setData);
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSocial = (k, v) => setData((d) => ({ ...d, socials: { ...(d.socials || {}), [k]: v } }));
  const setStat = (i, k, v) => {
    const stats = [...(data.stats || [])];
    stats[i] = { ...stats[i], [k]: v };
    set("stats", stats);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.hero.put(data);
      setData(updated);
      toast.success("Hero updated");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="space-y-3">{[0,1,2].map((i)=>(<div key={i} className="glass rounded-2xl h-24 animate-pulse" />))}</div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-hero">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-1">CMS</div>
          <h1 className="font-display text-3xl text-white font-medium">Hero Section</h1>
          <p className="text-slate-400 text-sm mt-1">Edit the headline, summary and badges shown on the home page.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="admin-hero-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black text-sm font-medium disabled:opacity-70"
        >
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving</> : <><Save size={14} /> Save</>}
        </button>
      </div>

      <Section title="Identity">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Name</Label><TextInput value={data.name} onChange={(v) => set("name", v)} /></div>
          <div><Label>First name</Label><TextInput value={data.first_name} onChange={(v) => set("first_name", v)} /></div>
          <div><Label>Title</Label><TextInput value={data.title} onChange={(v) => set("title", v)} /></div>
          <div><Label>Location</Label><TextInput value={data.location} onChange={(v) => set("location", v)} /></div>
          <div><Label>Email</Label><TextInput value={data.email} onChange={(v) => set("email", v)} /></div>
          <div><Label>Phone</Label><TextInput value={data.phone} onChange={(v) => set("phone", v)} /></div>
        </div>
      </Section>

      <Section title="Headline">
        <Label>Badge text</Label>
        <TextInput value={data.badge_text} onChange={(v) => set("badge_text", v)} />
        <div className="mt-4"><Label>Tagline</Label><TextInput value={data.tagline} onChange={(v) => set("tagline", v)} /></div>
        <div className="mt-4"><Label>Summary</Label><TextArea value={data.summary} onChange={(v) => set("summary", v)} rows={4} /></div>
        <div className="mt-4"><Label>"Currently" line</Label><TextArea value={data.currently_text} onChange={(v) => set("currently_text", v)} rows={2} /></div>
      </Section>

      <Section title="Avatar & Resume">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Avatar image</Label>
            <ImageUploader value={data.avatar_url} onChange={(v) => set("avatar_url", v)} />
            <p className="mt-2 text-xs text-slate-500">Or paste a URL:</p>
            <TextInput value={data.avatar_url} onChange={(v) => set("avatar_url", v)} />
          </div>
          <div>
            <Label>Resume URL</Label>
            <TextInput value={data.resume_url} onChange={(v) => set("resume_url", v)} />
            <p className="mt-2 text-xs text-slate-500">Manage the PDF from the Resume tab.</p>
          </div>
        </div>
      </Section>

      <Section title="Stats (4 chips)">
        <div className="grid sm:grid-cols-2 gap-3">
          {(data.stats || []).map((s, i) => (
            <div key={i} className="p-3 rounded-xl border border-white/10 bg-white/[0.02]">
              <Label>Value</Label>
              <TextInput value={s.value} onChange={(v) => setStat(i, "value", v)} />
              <div className="mt-3"><Label>Label</Label>
                <TextInput value={s.label} onChange={(v) => setStat(i, "label", v)} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => set("stats", [...(data.stats || []), { label: "", value: "" }])}
            className="text-cyan-300 text-xs hover:text-cyan-200"
          >+ Add stat</button>
          {data.stats?.length > 0 && (
            <button
              onClick={() => set("stats", data.stats.slice(0, -1))}
              className="text-slate-500 text-xs hover:text-red-400"
            >Remove last</button>
          )}
        </div>
      </Section>

      <Section title="Social links">
        <div className="grid sm:grid-cols-2 gap-3">
          {["github", "linkedin", "twitter", "leetcode", "codeforces", "codechef", "youtube"].map((k) => (
            <div key={k}>
              <Label>{k}</Label>
              <TextInput value={data.socials?.[k] || ""} onChange={(v) => setSocial(k, v)} placeholder={`https://${k}.com/...`} />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Hero;
